/**
 * TourStopsListPage - Admin list view of all tour stops
 *
 * SDA-125: server-side sort (removed client-side in-memory sort)
 * SDA-126: 50 rows per page default
 * SDA-127: First/Last pagination via shared <Pagination />
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getTourStops,
  getShows,
  getCities,
  markTourStopSoldOut,
  type TourStop,
  type Show,
  type City,
} from '../../../api/admin';
import SortableHeader from '../../../components/admin/SortableHeader';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import Pagination from '../../../components/admin/Pagination';

const LIMIT = 50;

type TourStopSortField =
  | 'start_date'
  | 'show_name'
  | 'city_name'
  | 'bookings_count'
  | 'is_active';

export default function TourStopsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tourStops, setTourStops] = useState<TourStop[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [showFilter, setShowFilter] = useState(searchParams.get('show') || '');
  const [cityFilter, setCityFilter] = useState(searchParams.get('city') || '');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'sold_out'>(
    (searchParams.get('status') as 'all' | 'active' | 'inactive' | 'sold_out') || 'all'
  );
  // SDA-178: default to upcoming; persist in URL so back-nav restores state
  const [timeFilter, setTimeFilter] = useState<'upcoming' | 'past' | 'all'>(
    (searchParams.get('time') as 'upcoming' | 'past' | 'all') || 'upcoming'
  );
  const [page, setPage] = useState(1);

  // Server-side sorting (SDA-125)
  const [sortBy, setSortBy] = useState<TourStopSortField>('start_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    const f = field as TourStopSortField;
    if (sortBy === f) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      setPage(1);
    } else {
      setSortBy(f);
      setSortOrder('asc');
      setPage(1);
    }
  };

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadTourStops();
    // Update URL params
    const params = new URLSearchParams();
    if (showFilter) params.set('show', showFilter);
    if (cityFilter) params.set('city', cityFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (timeFilter !== 'upcoming') params.set('time', timeFilter);
    setSearchParams(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter, cityFilter, statusFilter, timeFilter, page, sortBy, sortOrder]);

  const loadFilters = async () => {
    try {
      const [showsData, citiesData] = await Promise.all([
        getShows(),
        getCities(),
      ]);
      setShows(showsData);
      setCities(citiesData);
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  };

  const loadTourStops = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { tour_stops, total: totalCount } = await getTourStops({
        show_id: showFilter || undefined,
        city_id: cityFilter || undefined,
        is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        is_sold_out: statusFilter === 'sold_out' ? true : undefined,
        time_filter: timeFilter,
        page,
        limit: LIMIT,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setTourStops(tour_stops);
      setTotal(totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tour stops');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSoldOut = async (tourStop: TourStop) => {
    try {
      await markTourStopSoldOut(tourStop.id, !tourStop.is_sold_out);
      await loadTourStops();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tour stop');
    }
  };

  const formatDateRange = (start: string, end: string) => {
    // Parse date-only strings (YYYY-MM-DD) as local noon to avoid UTC midnight
    // rendering the prior day in US timezones. Same pattern as FacultyPayoutsPage.
    const parseDate = (s: string) => new Date(s.includes('T') ? s : s + 'T12:00:00');
    const startDate = parseDate(start);
    const endDate = parseDate(end);
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

    if (startDate.getFullYear() !== endDate.getFullYear()) {
      return `${startDate.toLocaleDateString('en-US', { ...opts, year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
    }

    return `${startDate.toLocaleDateString('en-US', opts)} - ${endDate.toLocaleDateString('en-US', opts)}, ${endDate.getFullYear()}`;
  };

  if (isLoading && tourStops.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Tour Stops</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Manage tour dates and venues for all shows.
          </p>
        </div>
        <Link to="/admin/tour-stops/new" className="btn-primary min-h-[44px] flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">Add </span>Tour Stop
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:flex gap-3">
          {/* Show Filter */}
          <div className="w-full sm:min-w-[200px] md:flex-1">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Show
            </label>
            <SearchableSelect
              value={showFilter}
              placeholder="All Shows"
              onChange={(val) => {
                setShowFilter(val);
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Shows' },
                ...shows.map((show) => ({ value: show.id, label: show.name })),
              ]}
            />
          </div>

          {/* City Filter */}
          <div className="w-full sm:min-w-[200px] md:flex-1">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              City
            </label>
            <SearchableSelect
              value={cityFilter}
              placeholder="All Cities"
              onChange={(val) => {
                setCityFilter(val);
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Cities' },
                ...cities.map((city) => ({ value: city.id, label: `${city.name}, ${city.state}` })),
              ]}
            />
          </div>

          {/* Status Filter */}
          <div className="w-full sm:min-w-[150px] md:flex-1">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as typeof statusFilter);
                setPage(1);
              }}
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="sold_out">Sold Out</option>
            </select>
          </div>

          {/* Time Filter (SDA-178) */}
          <div className="w-full sm:min-w-[150px] md:flex-1">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Time
            </label>
            <select
              value={timeFilter}
              onChange={(e) => {
                setTimeFilter(e.target.value as typeof timeFilter);
                setPage(1);
              }}
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
              <option value="all">All Time</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(showFilter || cityFilter || statusFilter !== 'all' || timeFilter !== 'upcoming') && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setShowFilter('');
                  setCityFilter('');
                  setStatusFilter('all');
                  setTimeFilter('upcoming');
                  setPage(1);
                }}
                className="px-4 py-2.5 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Tour Stops List */}
      {tourStops.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No tour stops found</h3>
          <p className="text-warm-600 mb-6">
            {showFilter || cityFilter || statusFilter !== 'all' || timeFilter !== 'upcoming'
              ? 'Try adjusting your filters to see more results.'
              : 'Create your first tour stop to get started.'}
          </p>
          <Link to="/admin/tour-stops/new" className="btn-primary">
            Add Tour Stop
          </Link>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Show
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Venue / City
                  </th>
                  <SortableHeader
                    label="Dates"
                    field="start_date"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Bookings
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Waitlist
                  </th>
                  <SortableHeader
                    label="Status"
                    field="is_active"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {tourStops.map((stop) => (
                  <tr key={stop.id} className="hover:bg-warm-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {stop.show?.logo_url ? (
                          <img
                            src={stop.show.logo_url}
                            alt={stop.show.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-bold">
                              {stop.show?.name.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                        <span className="font-medium text-warm-900">
                          {stop.show?.name || 'Unknown Show'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-warm-900">
                        {stop.theater?.name || 'Unknown Venue'}
                      </p>
                      <p className="text-sm text-warm-500">
                        {stop.city?.name}, {stop.city?.state}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-warm-700">
                        {formatDateRange(stop.start_date, stop.end_date)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {(stop.class_count || 0) > 0 ? (
                        <Link
                          to={`/admin/bookings?tour_stop=${stop.id}`}
                          className="block hover:bg-primary-50 -mx-2 -my-1 px-2 py-1 rounded-lg transition-colors"
                        >
                          <span className="text-primary-600 font-medium">
                            {stop.class_count} {stop.class_count === 1 ? 'class' : 'classes'}
                          </span>
                          <p className="text-xs text-warm-500">
                            {stop.booking_count || 0} {(stop.booking_count || 0) === 1 ? 'studio' : 'studios'}
                          </p>
                        </Link>
                      ) : (
                        <span className="text-warm-400">No bookings</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {(stop.waitlist_count || 0) > 0 ? (
                        <span className="text-warning-600 font-medium">
                          {stop.waitlist_count} &#9203;
                        </span>
                      ) : (
                        <span className="text-warm-400">0</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {stop.is_sold_out && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-700">
                            Sold Out
                          </span>
                        )}
                        {!stop.is_active && !stop.is_sold_out && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
                            Inactive
                          </span>
                        )}
                        {stop.is_active && !stop.is_sold_out && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
                            Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/tour-stops/${stop.id}/edit`}
                          className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleToggleSoldOut(stop)}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            stop.is_sold_out
                              ? 'text-success-600 hover:text-success-700 hover:bg-success-50'
                              : 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
                          }`}
                        >
                          {stop.is_sold_out ? 'Reopen' : 'Sold Out'}
                        </button>
                        <Link
                          to={`/admin/tour-stops/new?clone=${stop.id}`}
                          className="px-3 py-1.5 text-sm text-warm-600 hover:text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
                        >
                          Clone
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {tourStops.map((stop) => (
              <Link
                to={`/admin/tour-stops/${stop.id}/edit`}
                key={stop.id}
                className="block bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300"
              >
                <div className="flex items-center gap-3 mb-2">
                  {stop.show?.logo_url ? (
                    <img
                      src={stop.show.logo_url}
                      alt={stop.show.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-bold">
                        {stop.show?.name.charAt(0) || '?'}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-warm-900 truncate">
                      {stop.show?.name || 'Unknown Show'}
                    </p>
                    <p className="text-sm text-warm-500 truncate">
                      {stop.theater?.name || 'Unknown Venue'} &middot; {stop.city?.name}, {stop.city?.state}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-warm-700 mb-2">
                  {formatDateRange(stop.start_date, stop.end_date)}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-warm-600">
                      {stop.class_count || 0} {(stop.class_count || 0) === 1 ? 'class' : 'classes'}
                    </span>
                    <span className="text-warm-400">&middot;</span>
                    <span className="text-warm-600">
                      {stop.booking_count || 0} {(stop.booking_count || 0) === 1 ? 'studio' : 'studios'}
                    </span>
                    {(stop.waitlist_count || 0) > 0 && (
                      <>
                        <span className="text-warm-400">&middot;</span>
                        <span className="text-warning-600">
                          Waitlist: {stop.waitlist_count}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {stop.is_sold_out && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-700">
                        Sold Out
                      </span>
                    )}
                    {!stop.is_active && !stop.is_sold_out && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
                        Inactive
                      </span>
                    )}
                    {stop.is_active && !stop.is_sold_out && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
                        Active
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination (SDA-127) — First/Last buttons via shared component */}
          <Pagination
            page={page}
            total={total}
            limit={LIMIT}
            itemLabel="tour stops"
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
